// The Requests list (`#requests`, lane B2 S08; replaces V1's RequestsList): the requests this person has a seat on,
// one tab per status group the server sections for their seats (`request.list`; never filtered by role here, V1's N02
// rule). Create request is drawn only when `request.create` is callable ("Create request: HRBP Yes; all others No.").
// An empty group is hidden, and with none left the page reads his sentence for where requests come from (F-18), never a
// bare table; a refused list is a red banner with his sentence and no Retry; a failure is the error strip.
// Hosted by MissionShell: views/b2/register.ts registers it through S12's registerView, so it draws no McRoot of its own.
// The heading, and with it the document title, is the page's own nav label from `shell.nav`, *Hiring Requests* (D-3's
// word for this page; F-90), so the rail, the crumb and the heading read alike; *Requests* only until the nav has loaded.
// B13 (F-148, DC-234): a converted request stays in its tab, its status reading *Converted → R-####*, a link to the
// requisition. The list row names no requisition yet (C9 froze `request.list`), so the row reads the request's own record
// (`request.open`, the cache the request page shares) for the id until the row carries `converted_requisition_id`.

import { useEffect } from "react";
import { requestList, requestOpen, type RequestQueueRow, type RequestSection } from "@/api/tools";
import { go } from "@/router";
import { useSession } from "@/session/useSession";
import { navTitle, useShellNav } from "@/shell/nav";
import { useViewState } from "@/shell/viewState";
import {
  Banner, Button, Due, EmptyState, ErrorStrip, G6_CAPABILITY_LABELS, PageHeader, Pill, REQUEST_STATUS, REQUISITION_STATUS,
  ROLE_LABELS, RULE_LABELS, SLA_TIER_LABELS, Stack, Table, Tabs, Td, TextLink, Th, Tr, formatDate,
} from "@/mc";
import { refusalSentence, STATUS_WORD, useSettledRead } from "./data";
import { ConvertedLink } from "./HiringRequest";
import { REQUEST_FIELD_LABELS as F, REQUEST_PAGE_LABELS as P, REQUEST_RULES as R } from "./labels";

/** The server's section keys → his status words (G §5 chains). */
const SECTION_WORD: Readonly<Record<string, string>> = {
  draft: REQUEST_STATUS.draft,
  pending_hm_cert: REQUEST_STATUS.pendingHmCertification,
  pending_hrbp_approval: REQUEST_STATUS.pendingHrbpApproval,
  approved: REQUEST_STATUS.approved,
  dormant: REQUEST_STATUS.dormant,
  req_creation_pending: REQUISITION_STATUS.reqCreationPending,
};

const SEAT_WORD: Readonly<Record<string, string>> = {
  hrbp: ROLE_LABELS.hrbpAbbr,
  hm: ROLE_LABELS.hmAbbr,
  coordinator: ROLE_LABELS.coordinator,
  recruiter: ROLE_LABELS.recruiter,
  ta_manager: ROLE_LABELS.taManager,
  admin: ROLE_LABELS.admin,
};

export function RequestsList() {
  return <RequestsBody />;
}

function RequestsBody() {
  const { can } = useSession();
  const { register } = useViewState();
  const list = useSettledRead(["request", "list"], () => requestList({ page_size: 100 }));
  const nav = useShellNav({ poll: false }); // the shell polls it; this reads the same cache
  const count = (list.data?.sections ?? []).filter((section) => section.total > 0).length;
  useEffect(() => register({ view: "requests", ids: {}, summary: { loading: list.isLoading, count } }), [register, list.isLoading, count]);
  const refused = list.error ? refusalSentence(list.error) : null;

  return (
    <Stack gap={16}>
      <PageHeader
        title={navTitle(nav.data, "requests") ?? P.requests}
        actions={(
          <Button tone="primary" icon="plus" can={can("request.create")} onClick={() => go("#requests/new")} >
            {G6_CAPABILITY_LABELS.createRequest}
          </Button>
        )}
      />
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      {list.error && !refused ? (
        <ErrorStrip
          message={RULE_LABELS.failedSyncRetryable}
          problem={{ status: list.error.status, refusal_code: list.error.code, request_id: list.error.requestId }}
          onRetry={() => void list.refetch()}
          
        />
      ) : null}
      {list.isLoading ? <Table label={P.requests} loading={4} cols={4} /> : null}
      {list.data ? <RequestSections sections={list.data.sections} /> : null}
    </Stack>
  );
}

/** One tab per section holding a request; with none, his sentence for where requests come from (F-18). */
export function RequestSections({ sections }: { sections: readonly RequestSection[] }) {
  const shown = sections.filter((section) => section.total > 0);
  if (shown.length === 0) return <EmptyState>{R.hrbpCreates}</EmptyState>;
  const keys = shown.map((section) => section.key);
  const tabs = shown.map((section) => {
    const word = SECTION_WORD[section.key] ?? section.label;
    const shared = keys.filter((key) => key === section.key).length > 1;
    return {
      key: `${section.seat}.${section.key}`,
      label: shared ? `${word} · ${SEAT_WORD[section.seat] ?? section.seat}` : word,
      count: section.total,
      hs: `req.tab.${section.seat}.${section.key.replace(/_/g, "-")}`,
      panel: <SectionTable section={section} />,
    };
  });
  return <Tabs label={P.requests} items={tabs} />;
}

function SectionTable({ section }: { section: RequestSection }) {
  return (
    <Table
      dense
      label={SECTION_WORD[section.key] ?? section.label}
      cols={4}
      head={(
        <Tr>
          <Th>{F.title}</Th>
          <Th>{F.department}</Th>
          <Th>{P.status}</Th>
          <Th>{SLA_TIER_LABELS.due}</Th>
        </Tr>
      )}
    >
      {section.items.map((row, index) => <RequestRow key={row.id} row={row} section={section.key} index={index} />)}
    </Table>
  );
}

function RequestRow({ row, section, index }: { row: RequestQueueRow; section: string; index: number }) {
  const clock = row.clock as { due_at?: string; overdue?: boolean } | null;
  const slot = `${section.replace(/_/g, "-")}.${index}`;
  return (
    <Tr >
      <Td label={F.title} sub={<span className="mc-mono">{row.id}</span>}>
        <TextLink href={`#requests/${row.id}`} >{row.title || row.id}</TextLink>
      </Td>
      <Td label={F.department}>{row.department || "—"}</Td>
      <Td label={P.status}>
        {row.status === "converted"
          ? <ConvertedStatus row={row}  />
          : <Pill>{STATUS_WORD[row.status] ?? row.status}</Pill>}
      </Td>
      <Td label={SLA_TIER_LABELS.due}>
        {clock?.due_at && (row.status === "submitted" || row.status === "question_raised")
          ? <Due tier={clock.overdue ? "overdue" : "due"} phrase={formatDate(clock.due_at)} at={clock.due_at} />
          : "—"}
      </Td>
    </Tr>
  );
}

/** A converted row's status (F-148): *Converted → R-####*, linked to the requisition; the request's word until the
 * requisition's id is known. */
export function ConvertedStatus({ row, hs }: { row: RequestQueueRow & { converted_requisition_id?: string | null }; hs?: string | undefined }) {
  const carried = row.converted_requisition_id ?? null;
  const q = useSettledRead(["request", row.id], () => requestOpen({ request_id: row.id }), { enabled: !carried });
  const requisitionId = carried ?? q.data?.converted_requisition_id ?? null;
  return requisitionId ? <ConvertedLink requisitionId={requisitionId} hs={hs} /> : <Pill>{STATUS_WORD[row.status] ?? row.status}</Pill>;
}
